import type { AgentView } from '../shared/contracts';

export function agentPhoneKey(phone: string | null | undefined) {
  const clean = (phone ?? '').replace(/[()\s.-]/g, '');
  if (/^\+[1-9]\d{7,14}$/.test(clean)) return clean.slice(1);
  if (/^\d{10}$/.test(clean)) return `1${clean}`;
  return /^1\d{10}$/.test(clean) ? clean : '';
}
export function agentPhoneLabel(phone: string | null | undefined) {
  const key = agentPhoneKey(phone);
  return /^1\d{10}$/.test(key) ? `(${key.slice(1,4)}) ${key.slice(4,7)}-${key.slice(7)}` : phone || 'No phone in FUB';
}
export function agentPhoneCounts(agents: AgentView[]) {
  const counts: Record<string, number> = {};
  for (const agent of new Map(agents.map(agent => [agent.id, agent])).values()) {
    const key = agentPhoneKey(agent.phone);
    if (key) counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}
function comparePhones(a: AgentView, b: AgentView, descending: boolean) {
  const first = agentPhoneKey(a.phone), second = agentPhoneKey(b.phone);
  if (!first || !second) return first ? -1 : second ? 1 : 0;
  return first.localeCompare(second) * (descending ? -1 : 1);
}

// Default FUB team 1 covers the entire account. Prefer the agent's specific teams for display.
export function teamLabel(agent: AgentView) {
  const teams = agent.teams ?? [];
  const specific = teams.filter(t => !t.isDefault);
  return (specific.length ? specific : teams).map(t => t.name).sort((a,b) => a.localeCompare(b)).join(', ') || 'No team membership';
}
export function teamOptions(agents: AgentView[]) {
  const options = new Map<string, {id:string;name:string;accountLabel:string;isDefault:boolean;count:number}>();
  for (const agent of agents) for (const team of agent.teams ?? []) {
    const existing = options.get(team.id);
    if (existing) existing.count++;
    else options.set(team.id,{...team,accountLabel:agent.accountLabel,count:1});
  }
  const names = new Map<string, number>();
  for (const t of options.values()) names.set(t.name,(names.get(t.name) ?? 0)+1);
  return [...options.values()].map(t => ({...t,label:`${t.name}${(names.get(t.name) ?? 0)>1 ? ` · ${t.accountLabel}` : ''}${t.isDefault ? ' · entire account' : ''}`})).sort((a,b)=>a.name.localeCompare(b.name)||a.id.localeCompare(b.id));
}
export function filterAndSortAgents(agents: AgentView[], teamId: string, search: string, sort: string, status = 'all', pause = 'all', zipCounts: Record<string, number | null> = {}) {
  const query=search.trim().toLowerCase();
  const phoneQuery=/^[+()\d\s.-]+$/.test(query) ? query.replace(/\D/g,'') : '';
  return agents.filter(a => (status==='all'||(status==='active'?a.status==='Active':a.status!=='Active'))
    && (pause==='all'||(pause==='paused'?a.paused===true:pause==='unpaused'?a.paused===false:a.paused===null))
    && (teamId==='all' || (teamId==='unassigned' ? !a.teams?.length : a.teams?.some(t=>t.id===teamId)))
    && (`${a.name} ${a.email} ${a.phone ?? ''} ${(a.teams ?? []).map(t=>t.name).join(' ')}`.toLowerCase().includes(query) || !!phoneQuery && agentPhoneKey(a.phone).includes(phoneQuery)))
    .sort((a,b) => (sort==='phone-asc'||sort==='phone-desc' ? comparePhones(a,b,sort==='phone-desc') : sort==='zips-desc'||sort==='zips-asc' ? (zipCounts[a.id] == null ? (zipCounts[b.id] == null ? 0 : 1) : zipCounts[b.id] == null ? -1 : (zipCounts[a.id]!-zipCounts[b.id]!)*(sort==='zips-asc'?1:-1)) : sort==='team' ? teamLabel(a).localeCompare(teamLabel(b)) : sort==='capacity' ? b.settings.weeklyCap-a.settings.weeklyCap : 0)
      || a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}
